<template>
  <div class="search-project">
    <div class="search-bar">
      <span class="is-flex">
        <input type="text" :placeholder="$t('projectPage.searchWithinProject')" class="search-field input" v-model="tempSearchQuery" @keyup.enter="search" ref="search" autocomplete="off" data-lpignore="true">
        <button @click="search" class="button is-primary is-uppercase is-size-7 has-text-weight-bold search-submit" type="submit">{{$t('projectPage.search')}}</button>
      </span>
      <span v-if="searchError" class="help is-danger animated fadeIn">{{searchError}}</span>
    </div>
    <div class="search-landing" v-if="!populatedResults.length > 0">
      <img v-if="!isSearching" :src="logo" :alt="logoAlt">
      <div class="animated fadeOut" v-if="isSearching">
        <preloader :searchTerm="searchQuery" mode='animate'></preloader>
      </div>
    </div>
    <div v-if="!isSearching" class="animated slideInUp">
      <div v-for="(result, index) in populatedResults" :key="index" class="search-card is-flex cursor-pointer" @click="(openSearchResult(result))">
        <div class="image" v-if="shouldShowImage(result)">
          <img :src="result.post.attachments[0].url" />
        </div>
        <div class="body">
          <span class="result-type is-pulled-right has-text-weight-bold is-uppercase is-size-7 has-text-grey-light">{{ result.comment ? $t('projectPage.comment') : $t('projectPage.section') }}</span>
          <span class="user" style="display: flex">
            <span class="avatar cursor-pointer"><img width="40" height="40" :src="thumbnail(getProfileImage(result))"></span>
            <label class="label cursor-pointer">&nbsp;{{result.comment ? result.comment.user.userName : result.post.user.userName}}</label>
          </span>
          <label v-line-clamp="2" class="cursor-pointer" v-html="sanitizeContent(result)"></label>
        </div>
      </div>
    </div>
    <grid-modal :postID="selectedPostID" v-if="showModal"> </grid-modal>
  </div>
</template>

<script>

import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import Preloader from '@/components/Controls/Preloader'
import ModalCustom from '@/components/Modals/Custom'

import imageProxy from '@/libs/image-proxy'
import componentState from '@/mixins/component-state'

import state from '../state'
import mixin from '../tabs'
import GridModal from '../Post/Gridmodal'
import i18n from '@/i18n'

export default {
  components: {
    Preloader,
    InfiniteScroll,
    ModalCustom,
    GridModal
  },
  mixins: [componentState, mixin],

  data: function () {
    return {
      showDeletedModal: false,
      showSearch: false,
      tempSearchQuery: '',
      searchQuery: '',
      searchError: '',
      showResults: false,
      results: [],
      isSearching: false,
      selectedPostID: '',
      selectedCommentID: ''
    }
  },
  mounted: function () {
    this.$refs.search.focus()
  },
  computed: {
    appName: function () {
      return process.env.APP_NAME.toLowerCase()
    },
    showModal: function () {
      return this._get(state.grid.modalVisible) && this.selectedPostID !== 0
    },
    populatedResults: function () {
      return this.results.filter(function (r) {
        return Object.keys(r).length !== 0
      })
    },
    logo: function () {
      return this.appName === 'make' ? '/static/makey-logo-grey.png' : '/static/logo-grey.svg'
    },
    logoAlt: function () {
      return this.appName === 'neweng' ? 'Greyed out engineering.com logo' : 'Greyed out makeprojects.com logo'
    }
  },
  methods: {
    search: function (e) {
      this.$refs.search.blur()
      this.searchQuery = this.tempSearchQuery.trim()
      this.isSearching = true
      this.searchError = ''
      this.results = []
      this.$store.dispatch('searchInProject', { id: this.project.id, searchQuery: this.searchQuery }).then((response) => {
        if (response.total > 0) {
          this.isSearching = false
          this.results = response.contributions
          if (this.populatedResults.length <= 0) {
            this.updateSearchError()
          }
        } else {
          this.isSearching = false
          this.updateSearchError()
        }
      }, () => {
        this.isSearching = false
        this.updateSearchError()
      })
    },
    updateSearchError: function () {
      this.searchError = i18n.t('projectPage.noSearchResults') + this.searchQuery + i18n.t('projectPage.noSearchResultsClosingBracket')
    },
    close: function () {
      this.showResults = false
      this.results = []
    },
    toggleModal: function (value) {
      this._set(state.grid.modalVisible, value)
    },
    openSearchResult: function (result) {
      if (result.comment) {
        this.selectedPostID = result.comment.postID
        this.selectedCommentID = result.comment.id
        this._set(state.comments.scrollTo(this.selectedPostID), parseInt(this.selectedCommentID))
      } else {
        this.selectedPostID = result.post.id
      }

      this.$store.dispatch('getPost', { id: this.selectedPostID, force: false }).then(() => {
        this.toggleModal(true)
      })
    },
    shouldShowImage: function (result) {
      return result.post && result.post.attachments && result.post.attachments.length > 0 && result.post.attachments[0].type === 'image'
    },
    getProfileImage: function (result) {
      return result.comment ? result.comment.user.avatar : result.post.user.avatar
    },
    sanitizeContent: function (result) {
      return result.comment ? this.filterQuill(result.comment.body) : this.filterQuill(result.post.body)
    },
    highlight: function (body) {
      return body.replace(new RegExp(this.searchQuery, 'gi'), match => {
        return '<span style="font-weight: bold; color: #7A2A90;">' + match + '</span>'
      })
    },
    truncate: function (body) {
      let queryPosition = ''
      let mentionText = []

      const isMention = body && body.mention && body.mention.value

      if (isMention) {
        const pattern = new RegExp(/<a\b[^>]*>(.*)/)
        mentionText = pattern.exec(body.mention.value)
        queryPosition = mentionText[1].toLowerCase().indexOf(this.searchQuery.toLowerCase())
      } else {
        queryPosition = body.toLowerCase().indexOf(this.searchQuery.toLowerCase())
      }

      // Pad the search term with ~30 characters (to the nearest word) in front
      const newBody = isMention ? mentionText : body.substring(queryPosition - 30)
      var firstSpace = newBody.indexOf(' ')
      if (queryPosition >= 30) {
        var truncated = true
      }

      if (isMention || body.indexOf(this.searchQuery) === -1) {
        if (typeof body === 'object') {
          body = ''
        }
        body = body + ' ' + this.searchQuery
      }

      return truncated ? '...' + this.highlight(newBody.substring(firstSpace + 1)) : this.highlight(body)
    },
    filterQuill: function (content) {
      let cleanedContent = ''
      if (content.indexOf('quill') !== 0) {
        cleanedContent = content
      } else {
        cleanedContent = JSON.parse(content.substring('quill'.length)).ops[0].insert
      }
      return this.truncate(cleanedContent)
    },
    thumbnail: imageProxy.thumbnail
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.search-project {
  padding: 2rem;
  .search-landing {
    text-align: center;
    min-height: 344px;
    img {
      width: 25%;
      max-width: 10rem;
      padding-top: 25px;
    }
  }
  .search-field {
    border-radius: 4px 0px 0px 4px;
  }
  .search-submit {
    border-radius: 0px 4px 4px 0px;
    height: 36px;
  }
  .search-card {
    display: flex;
    border-radius: 5px;
    margin: 20px 0;
    -webkit-box-shadow: 0px 2px 5px 0px rgba(168,168,168,.5);
    -moz-box-shadow: 0px 2px 5px 0px rgba(168,168,168,.5);
    box-shadow: 0px 2px 5px 0px rgba(168,168,168,.5);

    .image {
      min-height: 150px;
      min-width: 150px;
      max-height: 150px;
      max-width: 150px;
      background: $secondary;
      justify-content: center;
      display: flex;
      flex-direction: row;
      overflow: hidden;
      img {
        flex: 1;
        height: 100%;
        object-fit: cover;
      }
    }
    .body {
      width: 100%;
      padding: 20px;
      .user {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        img {
          max-height: 40px;
          max-width: 40px;
          border-radius: 25px;
        }
      }
    }

    @include mobile {
      flex-direction: column;
      .image {
        max-width: 100%;
        min-width: 100%;
      }
    }
  }
}

</style>
